import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@codes/ui-kit/components/ui/sheet';
import { useToast } from '@codes/ui-kit/components/ui/use-toast';
import { Loader2 } from 'lucide-react';

import type { CreateKeyDTO, KeyView } from '@codes/openapi-keys';
import { useCreateOpenApiKey, useOpenApiKeyDetail, useUpdateOpenApiKey } from '@codes/openapi-keys';

import { maskApiKey } from '../utils';

interface OpenApiKeyFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: 'create' | 'edit';
    keyData: KeyView | null;
    onSuccess: (key?: { apiKey: string; name: string }) => void;
}

type FormErrors = {
    name?: string;
};

export const OpenApiKeyFormDialog = ({ open, onOpenChange, mode, keyData, onSuccess }: OpenApiKeyFormDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();

    const createMutation = useCreateOpenApiKey();
    const updateMutation = useUpdateOpenApiKey();

    const { data: detailData, isLoading: isLoadingDetail } = useOpenApiKeyDetail(keyData?.id || '', {
        enabled: mode === 'edit' && open && !!keyData?.id,
    });

    const formData = mode === 'edit' ? detailData || keyData : null;
    const [name, setName] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [mocks, setMocks] = useState(true);
    const [hidden, setHidden] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});

    useEffect(() => {
        if (open) {
            if (mode === 'edit' && formData) {
                setName(formData.name ?? '');
                setHidden(formData.hidden ?? false);
                setInvalid(formData.invalid ?? false);
            } else if (mode === 'create') {
                setName('');
                setApiKey('');
                setMocks(true);
                setHidden(false);
                setInvalid(false);
            }
            setErrors({});
        }
    }, [open, mode, formData]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (mode === 'create' && !name.trim()) {
            newErrors.name = t('openApiKeys.form.nameRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            if (mode === 'create') {
                const body: CreateKeyDTO = {
                    name: name.trim() || undefined,
                    apiKey: apiKey.trim() || undefined,
                };

                const result = await createMutation.mutateAsync({ body, mocks });

                if (!result.apiKey) {
                    throw new Error(t('openApiKeys.form.apiKeyNotReturned'));
                }

                toast({
                    title: t('openApiKeys.createSuccess'),
                });
                onOpenChange(false);
                onSuccess({ apiKey: result.apiKey, name: name.trim() || 'openapi-key' });
            } else if (mode === 'edit' && keyData?.id) {
                const body = {
                    name: name.trim() || undefined,
                    hidden,
                    invalid,
                };

                await updateMutation.mutateAsync({ id: keyData.id, body });
                toast({
                    title: t('openApiKeys.updateSuccess'),
                });
                onOpenChange(false);
                onSuccess();
            }
        } catch (error) {
            toast({
                title: t('openApiKeys.error'),
                description: error instanceof Error ? error.message : t('openApiKeys.form.unknownError'),
                variant: 'destructive',
            });
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    if (mode === 'edit' && isLoadingDetail) {
        return (
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent side="right" className="overflow-y-scroll w-[400px] sm:w-[540px]">
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="overflow-y-scroll w-[400px] sm:w-[540px]">
                <SheetHeader aria-describedby={undefined}>
                    <SheetTitle>
                        {mode === 'create' ? t('openApiKeys.createNew') : t('openApiKeys.form.edit')}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex flex-col gap-y-6 py-6">
                    <div className="text-gray-600 dark:text-gray-400">
                        {mode === 'create'
                            ? t('openApiKeys.form.createDescription')
                            : t('openApiKeys.form.editDescription')}
                    </div>

                    <form onSubmit={onSubmit} className="flex flex-col gap-y-6">
                        <div className="flex flex-col gap-y-2">
                            <Label htmlFor="name">
                                {t('openApiKeys.form.nameLabel')} {mode === 'create' && '*'}
                            </Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder={t('openApiKeys.form.namePlaceholder')}
                                disabled={isLoading}
                                autoComplete="off"
                            />
                            {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                {t('openApiKeys.form.nameDescription')}
                            </p>
                        </div>

                        {mode === 'create' && (
                            <>
                                <div className="flex flex-col gap-y-2">
                                    <Label htmlFor="apiKey">{t('openApiKeys.form.apiKeyOptionalLabel')}</Label>
                                    <Input
                                        id="apiKey"
                                        value={apiKey}
                                        onChange={e => setApiKey(e.target.value)}
                                        placeholder={t('openApiKeys.form.apiKeyPlaceholder')}
                                        disabled={isLoading}
                                        autoComplete="off"
                                    />
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        {t('openApiKeys.form.apiKeyDescription')}
                                    </p>
                                </div>

                                <div className="flex flex-col gap-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="mocks"
                                            type="checkbox"
                                            checked={mocks}
                                            onChange={e => setMocks(e.target.checked)}
                                            disabled={isLoading}
                                            className="h-4 w-4"
                                        />
                                        <Label htmlFor="mocks" className="cursor-pointer">
                                            Use mocks
                                        </Label>
                                    </div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        Enable mock mode for testing purposes
                                    </p>
                                </div>
                            </>
                        )}

                        {mode === 'edit' && (
                            <>
                                <div className="flex flex-col gap-y-2">
                                    <Label htmlFor="apiKey">{t('openApiKeys.form.apiKeyLabel')}</Label>
                                    <Input
                                        id="apiKey"
                                        value={maskApiKey(formData?.apiKey || '')}
                                        disabled
                                        className="bg-muted font-mono"
                                        autoComplete="off"
                                    />
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        {t('openApiKeys.form.apiKeyReadOnly')}
                                    </p>
                                </div>

                                <div className="flex flex-col gap-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="hidden"
                                            type="checkbox"
                                            checked={hidden}
                                            onChange={e => setHidden(e.target.checked)}
                                            disabled={isLoading}
                                            className="h-4 w-4"
                                        />
                                        <Label htmlFor="hidden" className="cursor-pointer">
                                            {t('openApiKeys.form.hiddenLabel')}
                                        </Label>
                                    </div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        {t('openApiKeys.form.hiddenDescription')}
                                    </p>
                                </div>

                                <div className="flex flex-col gap-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="invalid"
                                            type="checkbox"
                                            checked={invalid}
                                            onChange={e => setInvalid(e.target.checked)}
                                            disabled={isLoading}
                                            className="h-4 w-4"
                                        />
                                        <Label htmlFor="invalid" className="cursor-pointer">
                                            {t('openApiKeys.form.invalidLabel')}
                                        </Label>
                                    </div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        {t('openApiKeys.form.invalidDescription')}
                                    </p>
                                </div>

                                {(formData?.owner$ || formData?.createdAt) && (
                                    <div className="space-y-2 rounded-md border p-3 bg-muted/20">
                                        {formData?.owner$ && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-muted-foreground">
                                                    {t('openApiKeys.form.ownerLabel')}
                                                </span>
                                                <span>{formData.owner$.name || formData.owner$.id || ''}</span>
                                            </div>
                                        )}

                                        {formData?.createdAt && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-muted-foreground">
                                                    {t('openApiKeys.form.createdLabel')}
                                                </span>
                                                <span>{new Date(formData.createdAt).toLocaleString()}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        <SheetFooter className="pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                            >
                                {t('openApiKeys.deleteDialog.cancel')}
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {mode === 'create' ? t('openApiKeys.generate') : t('openApiKeys.update')}
                            </Button>
                        </SheetFooter>
                    </form>
                </div>
            </SheetContent>
        </Sheet>
    );
};
